// App.tsx

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { closeSnackbar, enqueueSnackbar, SnackbarProvider } from 'notistack';
import { useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import * as authMsal from './auth/authMsal';
import IdleLogout from './components/IdleLogout/IdleLogout';
import { ChatFeedbackToolbar } from './components/webComponents/ChatFeedbackToolbar';
import { AGENTIC_MS_USER_INFO, AGENTIC_MS_USER_SELECTED_THEME, DARK_THEME, IDLE_TIMEOUT_MS, THEME_BACKGROUND_DEFAULT_COLORS } from './constants/appConstants';
import { CHAT_FEEDBACK_TYPES, setChatFeedback } from './redux/slices/chatsFeedbackSlice';
import { setTheme } from './redux/slices/currentThemeSlice';
import { refreshView } from './redux/slices/transitionJourneySlice';
import store, { RootState } from './redux/store';
import lazyRoutes from './routes';
import { sendChatFeedback as endUserChatFeedback } from './services/endUserService/endUserService';
import { sendChatFeedback as l1ManagerChatFeedback } from './services/l1ManagerServices';
import { sendChatFeedback as transitionManagerChatFeedback } from './services/transitionManagerServices';
import { darkTheme, lightTheme } from './utlis/customTheme';
import ProtectedRoute from './utlis/protectedroutes';
import DisclosureBar from './components/disclosureBar/DisclosureBar';
import { MsalProvider } from '@azure/msal-react';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';

function App() {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state: RootState) => state.theme);
  const sessionData = useSelector((state: any) => state.session);
  const [isDarkMode, setIsDarkMode] = useState(currentTheme.theme === DARK_THEME);

  authMsal.setStore(store);

  useEffect(() => {
    let savedTheme = currentTheme.theme;
    const userSelectedTheme = localStorage.getItem(AGENTIC_MS_USER_SELECTED_THEME);
    if (userSelectedTheme) {
      savedTheme = userSelectedTheme
    }
    setIsDarkMode(savedTheme === DARK_THEME);
    dispatch(setTheme(savedTheme));
    dispatch(refreshView({ refresh: true }));
  }, [currentTheme]);


  useEffect(() => {
    const chatFeedbackHelper = new ChatFeedbackToolbar();
    const handleCopySuccess = (event: any) => {
      console.log('Copy Success:', event.detail.message);
      enqueueSnackbar({ variant: 'info', message: 'Message copied to clipboard!' })
    };

    const submitFeedback = (event: any) => {
      let submitChatFeedback: any = undefined;
      if (window.location.pathname.toLowerCase().indexOf('businessuser') !== -1) {
        submitChatFeedback = endUserChatFeedback;
      } else if (window.location.pathname.toLowerCase().indexOf('l1dashboard') !== -1) {
        submitChatFeedback = l1ManagerChatFeedback;
      } else if (window.location.pathname.toLowerCase().indexOf('transitionmanager') !== -1) {
        submitChatFeedback = transitionManagerChatFeedback;
      }
      if (submitChatFeedback) {
        const userSessionInfo: any = sessionStorage.getItem(AGENTIC_MS_USER_INFO);
        if (userSessionInfo) {
          const userInfo = JSON.parse(userSessionInfo);
          submitChatFeedback({
            userId: userInfo.email,
            chatId: event.detail.chatId,
            messageId: event.detail.messageId,
            traceId: event.detail.traceId,
            like: event.detail.liked,
            dislike: event.detail.disliked,
            comment: event.detail.comment
          })
            .catch(() => {
              console.error('Error in saving Transition Manager chat feedback');
            })
        }
      }
    }
    const handleLikeClick = (event: any) => {
      console.log('Like clicked:', event.detail.liked);
      dispatch(setChatFeedback({
        chatFeedback: {
          feedbackType: CHAT_FEEDBACK_TYPES.LIKE,
          chatId: event.detail.chatId,
          messageId: event.detail.messageId,
          like: event.detail.liked,
          dislike: event.detail.disliked
        }
      }));
      submitFeedback(event);
    };

    const handleDislikeClick = (event: any) => {
      console.log('Dislike clicked:', event.detail.disliked);
      dispatch(setChatFeedback({
        chatFeedback: {
          feedbackType: CHAT_FEEDBACK_TYPES.DISLIKE,
          chatId: event.detail.chatId,
          messageId: event.detail.messageId,
          like: event.detail.liked,
          dislike: event.detail.disliked
        }
      }));
      submitFeedback(event);
    };

    const handleCommentSubmit = (event: any) => {
      console.log('Comment submitted:', event.detail.comment);
      dispatch(setChatFeedback({
        chatFeedback: {
          feedbackType: CHAT_FEEDBACK_TYPES.COMMENT,
          chatId: event.detail.chatId,
          messageId: event.detail.messageId,
          comment: event.detail.comment
        }
      }));
      submitFeedback(event);
    };

    // Add event listeners
    window.addEventListener('copySuccess', handleCopySuccess);
    window.addEventListener('likeClick', handleLikeClick);
    window.addEventListener('dislikeClick', handleDislikeClick);
    window.addEventListener('commentSubmit', handleCommentSubmit);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener('copySuccess', handleCopySuccess);
      window.removeEventListener('likeClick', handleLikeClick);
      window.removeEventListener('dislikeClick', handleDislikeClick);
      window.removeEventListener('commentSubmit', handleCommentSubmit);
    };
  }, []);

  const theme = isDarkMode ? darkTheme : lightTheme;

  const NotFoundPage = () => {
    const handleBackToAppClick = () => {
      window.history.back();
    };

    return (
      <div style={{ textAlign: 'center', color: 'white' }}>
        <h1 style={{ fontSize: '2em', marginBottom: '20px' }}>Page Not Found</h1>
        <Button className="back-to-app-btn" onClick={handleBackToAppClick}>
          Back to Application
        </Button>
      </div>
    );
  };

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <MsalProvider instance={authMsal.msalInstance}>
          <CssBaseline />
          <SnackbarProvider anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
            hideIconVariant
            action={snackbarId => (
              <IconButton
                aria-label="close"
                color="inherit"
                onClick={() => closeSnackbar(snackbarId)}
                size="small"
                sx={{
                  padding: 0.5,
                  marginLeft: 1,
                  fontSize: 18,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          >
            <ErrorBoundary>
            <Box sx={{ width: '100vw', height: '100vh', backgroundColor: isDarkMode ? THEME_BACKGROUND_DEFAULT_COLORS.DARK : THEME_BACKGROUND_DEFAULT_COLORS.LIGHT }}>
              <BrowserRouter>
                {/* 👇 Idle logout watcher */}
                <IdleLogout idleTime={IDLE_TIMEOUT_MS * 60 * 1000} />
                <Routes>
                  {lazyRoutes.map(({ path, component: Component, allowedRoles }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <ProtectedRoute
                          // path={path}
                          component={<Component />}
                        // allowedRoles={allowedRoles}
                        />
                      }
                    />
                  ))}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <DisclosureBar />
              </BrowserRouter>
            </Box>
            </ErrorBoundary>
          </SnackbarProvider>
        </MsalProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;